import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { INK, MUTED_36, MUTED_60, FONT as YFONT, ROOMS, glow, capsuleStyle } from "@/lib/yuoDesign";
import RoomShell from "@/components/yuo/RoomShell";
import { useResponsive } from "@/lib/responsive";
import { MobileHeaderButton } from "@/components/mobile/MobileHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MicButton } from "@/components/MicButton";
import { HighlightedText } from "@/components/HighlightedText";
import { HighlightToolbar } from "@/components/HighlightToolbar";
import StoneTotem from "@/components/StoneTotem";
import StoneOrb from "@/components/yuo/StoneOrb";
import SiliconeButton from "@/components/SiliconeButton";

// YUO's three-color method — matches Mirror / DiaryEntry tokens exactly.

function formatDate(d) {
  try {
    const date = new Date(d);
    return (
      date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) +
      " · " +
      date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return "";
  }
}

export default function Diary() {
  const navigate = useNavigate();
  const { isPhone } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [voiceMeta, setVoiceMeta] = useState({ active: false });
  const [tab, setTab] = useState(searchParams.get("view") === "insights" ? "insights" : "entries");
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const textareaRef = useRef(null);

  const profile = user?.personalization_profile;
  const typeColor = profile?.body_color || COLORS.diary;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listDiary();
      setEntries(data || []);
    } catch (e) {
      console.warn("diary load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const data = await api.listInsights();
      setInsights(data || []);
    } catch (e) {
      console.warn("insights load", e);
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadInsights();
  }, [load, loadInsights]);

  const switchTab = (t) => {
    setTab(t);
    const p = new URLSearchParams(searchParams);
    if (t === "insights") p.set("view", "insights"); else p.delete("view");
    setSearchParams(p, { replace: true });
  };

  const [longPressEntry, setLongPressEntry] = useState(null);
  const longPressTimerRef = useRef(null);
  const startLongPress = (entry) => {
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => setLongPressEntry(entry), 550);
  };
  const cancelLongPress = () => { clearTimeout(longPressTimerRef.current); };

  const quickToMirror = async (entry) => {
    setLongPressEntry(null);
    try {
      const conv = await api.createConversation("mirror");
      await api.sendMessage(conv.conversation_id, `From my diary:\n\n${entry.content}\n\nHelp me look deeper.`);
      navigate("/mirror");
    } catch (e) { alert(e?.message || "Could not open Mirror"); }
  };
  const quickToScreen = (entry) => {
    setLongPressEntry(null);
    try { sessionStorage.setItem("yuo_screen_seed", entry.content); } catch {}
    navigate("/screenwriter");
  };
  const quickToTheologian = async (entry) => {
    setLongPressEntry(null);
    try {
      const conv = await api.createConversation("mirror");
      await api.sendMessage(conv.conversation_id, `YUO-theologian, break down this reflection through the lens of the world's texts:\n\n${entry.content}`);
      navigate("/mirror");
    } catch (e) { alert(e?.message || "Could not open theologian"); }
  };

  const generateInsight = async () => {
    if (!profile) { navigate("/personalization"); return; }
    setGenerating(true);
    try {
      const fresh = await api.generateInsight({ lang: "en" });
      setInsights((prev) => [fresh, ...prev]);
    } catch (e) {
      const msg = e?.detail?.message_en || e?.message || "Could not generate";
      alert(msg);
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await api.createDiary({
        content: body.trim(),
        via_voice: voiceMeta.active,
        voice_language: voiceMeta.language || null,
      });
      setBody("");
      setVoiceMeta({ active: false });
      setShowAdd(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <RoomShell
      room="diary"
      title="Diary"
      subtitle="entries"
      testId="diary-screen"
      actions={
        <>
          <button data-testid="add-diary-button" onClick={() => setShowAdd(true)}
            style={capsuleStyle({ active: true, room: "diary" })}>+ New</button>
        </>
      }
      mobileActions={
        <MobileHeaderButton room="diary" solid onClick={() => setShowAdd(true)} testId="add-diary-button-m">+ New</MobileHeaderButton>
      }
      showThemeToggle={false}
    >
      {/* Tabs — Entries / Insights (sticky so they stay pinned while the list scrolls) */}
      <div style={{
        position: "sticky", top: isPhone ? -6 : -10, zIndex: 5,
        margin: isPhone ? "-6px -14px 14px" : "-10px -24px 22px",
        padding: isPhone ? "8px 14px 10px" : "12px 24px 10px",
        display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        background: "var(--diary-tabs-bg)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}>
        <button data-testid="tab-entries" onClick={() => switchTab("entries")}
          style={capsuleStyle({ active: tab === "entries", room: "diary", idleTint: true })}>
          Entries
        </button>
        <button
          data-testid="tab-insights"
          onClick={() => switchTab("insights")}
          style={{
            ...capsuleStyle({ active: tab === "insights", room: "diary", idleTint: true }),
            padding: profile ? "4px 14px 4px 4px" : "8px 18px",
            gap: 10,
          }}>
          {profile && <StoneTotem color={typeColor} size={22} title={profile.type_name_en} />}
          <span>Insights{insights.length ? ` · ${insights.length}` : ""}</span>
        </button>
      </div>

      {tab === "insights" ? (
        <InsightsView
          insights={insights} loading={insightsLoading}
          onGenerate={generateInsight} generating={generating}
          profile={profile} typeColor={typeColor} navigate={navigate}
        />
      ) : loading ? (
        <div style={{ color: MUTED_36, fontSize: 11, letterSpacing: "0.32em", textAlign: "center", padding: 30, textTransform: "uppercase" }}>
          opening…
        </div>
      ) : entries.length === 0 ? (
        <div data-testid="diary-empty" style={{ color: MUTED_36, fontSize: 12, padding: 40, textAlign: "center", letterSpacing: "0.2em", textTransform: "uppercase" }}>
          no entries yet · tap + New
        </div>
      ) : (
        <div style={{ maxWidth: 780, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {entries.map((item) => (
            <button key={item.entry_id}
              onClick={() => navigate(`/diary/${item.entry_id}`)}
              onMouseDown={() => startLongPress(item)}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={() => startLongPress(item)}
              onTouchEnd={cancelLongPress}
              onTouchMove={cancelLongPress}
              onContextMenu={(e) => { e.preventDefault(); setLongPressEntry(item); }}
              data-testid={`diary-entry-${item.entry_id}`}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "22px 24px", borderRadius: 26, border: "none",
                background: glow(ROOMS.diary.rgb, 0.14),
                color: INK, fontFamily: YFONT, cursor: "pointer",
                animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
              }}>
              <div style={{ color: MUTED_36, fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10 }}>
                {formatDate(item.created_at)}
              </div>
              <div style={{
                color: INK, fontSize: 13.5, lineHeight: 1.65,
                display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>
                <HighlightedText content={item.content} />
              </div>
            </button>
          ))}
        </div>
      )}

      {longPressEntry && (
        <div data-testid="entry-quick-menu"
          onClick={() => setLongPressEntry(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: "var(--dk-3410-72)", backdropFilter: "blur(14px)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative", width: 300, height: 300,
              display: "flex", alignItems: "center", justifyContent: "center",
              animation: "yuo-emerge .55s cubic-bezier(.2,.7,.2,1) both",
            }}>
            {/* central orb — cancel */}
            <button data-testid="quick-cancel" onClick={() => setLongPressEntry(null)}
              style={{
                position: "absolute", width: 74, height: 74, borderRadius: "50%",
                border: "none", padding: 0,
                background: "radial-gradient(38% 34% at 34% 26%, #fff, var(--fg-18) 30%, #0d0f1c 65%, #05060c 100%)",
                boxShadow: "0 0 40px var(--fg-15), inset -6px -8px 16px rgba(0,0,0,0.85)",
                cursor: "pointer",
              }}
            />
            {/* Left — Screen (blue) */}
            <QuickOrb testId="quick-to-screen" onClick={() => quickToScreen(longPressEntry)}
              angle={180} label="Script" rgb="10,132,255" hex="#4F9DFF" />
            {/* Top — Mirror (mirror blue... use symbiosis green to differ) */}
            <QuickOrb testId="quick-to-mirror" onClick={() => quickToMirror(longPressEntry)}
              angle={-90} label="Mirror" rgb="52,255,150" hex="#34FF96" />
            {/* Right — Theologian (amber) */}
            <QuickOrb testId="quick-to-theologian" onClick={() => quickToTheologian(longPressEntry)}
              angle={0} label="Theologian" rgb="233,196,106" hex="#E9C46A" />
          </div>
        </div>
      )}

      {showAdd && (
        <div className="yuo-modal-backdrop" onClick={() => setShowAdd(false)}
          style={{ background: "var(--dk-3410-7)", backdropFilter: "blur(12px)" }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", left: 0, right: 0, bottom: 0,
              background: `radial-gradient(120% 100% at 22% 0%, rgba(${ROOMS.diary.rgb},0.16), rgba(${ROOMS.diary.rgb},0.03) 62%, transparent 100%), var(--yuo-bg)`,
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              padding: 22, minHeight: 460,
              display: "flex", flexDirection: "column", gap: 14,
              boxShadow: `0 -20px 60px rgba(${ROOMS.diary.rgb},0.14)`,
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ color: MUTED_36, fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase" }}>
                {formatDate(new Date().toISOString())}
              </div>
              <button onClick={() => setShowAdd(false)} data-testid="close-add-diary"
                style={capsuleStyle({ active: false, size: "sm" })}>Close</button>
            </div>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => { setBody(e.target.value); if (!e.target.value.trim()) setVoiceMeta({ active: false }); }}
              autoFocus data-testid="diary-body-input"
              placeholder="Select any word or phrase, then tap feeling · craft · growth to color it."
              style={{
                flex: 1, background: "var(--fg-04)", border: "none", outline: "none",
                borderRadius: 22, padding: 18,
                color: INK, fontFamily: YFONT, fontSize: 14, lineHeight: 1.65, minHeight: 240,
                boxShadow: `0 0 44px rgba(${ROOMS.diary.rgb},0.20) inset`,
              }}
            />
            <HighlightToolbar textareaRef={textareaRef} value={body} onChange={setBody} testIdPrefix="diary" />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
              <MicButton color={ROOMS.diary.hex}
                onTranscribed={(t, meta) => {
                  if (!t) return;
                  setBody((prev) => (prev.trim() ? `${prev.trim()}\n\n${t}` : t));
                  setVoiceMeta({ active: true, language: meta?.language });
                }}
                testID="diary-mic-button"
              />
              <button disabled={!body.trim() || saving} onClick={save} data-testid="save-diary-button"
                style={{
                  flex: 1, borderRadius: 999, padding: "14px 28px", border: "none",
                  fontFamily: YFONT, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                  background: `radial-gradient(120% 160% at 20% 0%, rgba(${ROOMS.diary.rgb},0.34), rgba(${ROOMS.diary.rgb},0.08))`,
                  boxShadow: `0 0 34px rgba(${ROOMS.diary.rgb},0.32)`,
                  color: INK, cursor: (body.trim() && !saving) ? "pointer" : "default",
                  opacity: (body.trim() && !saving) ? 1 : 0.5,
                }}>
                {saving ? "…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </RoomShell>
  );
}

const chipStyle = { border: "none", borderRadius: 999, padding: "6px 10px", display: "flex", alignItems: "center", justifyContent: "center" };

function QuickOrb({ testId, onClick, angle, label, rgb, hex }) {
  const rad = 105;
  const x = Math.round(Math.cos((angle * Math.PI) / 180) * rad);
  const y = Math.round(Math.sin((angle * Math.PI) / 180) * rad);
  return (
    <button data-testid={testId} onClick={onClick}
      style={{
        position: "absolute",
        transform: `translate(${x}px, ${y}px)`,
        width: 60, height: 60, borderRadius: "50%",
        border: "none", padding: 0, cursor: "pointer",
        background: `radial-gradient(38% 34% at 34% 26%, #fff, ${hex} 22%, rgba(${rgb},0.15) 62%, #05060c 100%)`,
        boxShadow: `0 0 26px rgba(${rgb},0.55), inset -5px -7px 14px rgba(0,0,0,0.85)`,
        color: "var(--yuo-ink)", fontFamily: '"JetBrains Mono", monospace',
        fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase",
      }}>
      <span style={{
        position: "absolute", top: "calc(100% + 10px)", left: "50%",
        transform: "translateX(-50%)",
        color: `rgba(${rgb},1)`, textShadow: `0 0 12px rgba(${rgb},0.6)`,
        whiteSpace: "nowrap",
      }}>{label}</span>
    </button>
  );
}

// -------------------------------------------------------------------------
// Insights tab — YUO's personal advice cards, tinted with the user's totem
// colour. Empty state invites completing UPGRADE first. Redesigned to the
// glow-capsule material — no more legacy 1px surface borders.
// -------------------------------------------------------------------------
function InsightsView({ insights, loading, onGenerate, generating, profile, typeColor, navigate }) {
  if (!profile) {
    return (
      <div data-testid="insights-empty-nopro" style={{
        padding: "40px 20px",
        textAlign: "center",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
        animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
      }}>
        <StoneOrb color="library" size={36} />
        <div style={{ color: COLORS.library, fontSize: 10, letterSpacing: "0.28em",
          textTransform: "uppercase", marginTop: 4 }}>
          Path of growth · Awareness
        </div>
        <div style={{ color: INK, fontFamily: YFONT, fontSize: 22,
          letterSpacing: -0.3 }}>
          Your Insights will live here
        </div>
        <div style={{ color: MUTED_60, fontSize: 13, lineHeight: 1.7,
          maxWidth: 460 }}>
          Insights are YUO's personal advice for you — coloured in your type's
          totem and read through your body, thinking style and current IQ. The
          UPGRADE diagnostic maps you first so every recommendation is yours.
        </div>
        <button
          data-testid="insights-open-upgrade"
          onClick={() => navigate("/personalization")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            marginTop: 8,
            padding: "12px 26px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.34), rgba(var(--role-yellow-rgb),0.08))",
            boxShadow: "0 0 30px rgba(var(--role-yellow-rgb),0.30)",
            color: "var(--lit-yellow-20)",
            fontFamily: YFONT,
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}>
          <StoneOrb color="library" size={14} />
          Begin the diagnostic
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <span className="yuo-spinner" style={{ color: typeColor }} />
      </div>
    );
  }

  const typeRgb = hexToRgbTriplet(typeColor);

  return (
    <div style={{ padding: "0 0 24px" }}>
      {/* Header capsule — cohesive glow, totem stone, PRO-aware "+ New" */}
      <div style={{
        padding: "18px 22px",
        borderRadius: 26,
        marginBottom: 18,
        background: `radial-gradient(120% 100% at 22% 0%, rgba(${typeRgb},0.18), rgba(${typeRgb},0.04) 62%, transparent 100%)`,
        boxShadow: `0 0 42px rgba(${typeRgb},0.16) inset`,
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <StoneTotem color={typeColor} size={44} title={profile.type_name_en} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: typeColor, fontSize: 10, letterSpacing: "0.24em",
            textTransform: "uppercase", marginBottom: 4, fontFamily: YFONT }}>
            Personal · UPGRADE-tuned
          </div>
          <div style={{ color: INK, fontFamily: YFONT,
            fontSize: 13, letterSpacing: 0, lineHeight: 1.55 }}>
            Read for {profile.type_name_en} at {profile.iq_level || "current"} IQ
          </div>
        </div>
        <button
          data-testid="generate-insight"
          onClick={onGenerate}
          disabled={generating}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "8px 16px",
            borderRadius: 999,
            border: "none",
            cursor: generating ? "default" : "pointer",
            background: `radial-gradient(120% 160% at 30% 0%, rgba(${typeRgb},0.30), rgba(${typeRgb},0.06))`,
            boxShadow: `0 0 22px rgba(${typeRgb},0.28)`,
            color: INK,
            fontFamily: YFONT,
            fontSize: 10,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            opacity: generating ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}>
          <StoneTotem color={typeColor} size={12} />
          {generating ? "…" : "+ New"}
        </button>
      </div>

      {insights.length === 0 ? (
        <div data-testid="insights-empty" style={{
          padding: "36px 20px",
          textAlign: "center",
          color: MUTED_60,
          fontSize: 13,
          lineHeight: 1.7,
          borderRadius: 26,
          background: `radial-gradient(120% 100% at 22% 0%, rgba(${typeRgb},0.10), rgba(${typeRgb},0.02) 62%, transparent 100%)`,
          boxShadow: `0 0 28px rgba(${typeRgb},0.10) inset`,
        }}>
          No insights yet. Tap "+ New" — YUO will read your recent entries and leave a note tuned to your UPGRADE map.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {insights.map((it) => (
            <InsightCard
              key={it.insight_id}
              insight={it}
              typeColor={typeColor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Hex ("#RRGGBB") → "r,g,b" triplet for rgba() interpolation.
function hexToRgbTriplet(hex) {
  const h = String(hex || "#B06BFF").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r},${g},${b}`;
}

// -------------------------------------------------------------------------
// Single insight card + "Note" (personal note the user can attach). All
// surfaces on the new glow material — no more legacy borders / inset lines.
// -------------------------------------------------------------------------
function InsightCard({ insight, typeColor }) {
  const c = insight.color || typeColor;
  const rgb = hexToRgbTriplet(c);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(insight.user_note || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!insight.user_note);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateInsightNote(insight.insight_id, note);
      setSaved(!!note.trim());
      setEditing(false);
    } catch (e) {
      alert(e?.message || "Could not save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`insight-${insight.insight_id}`}
      style={{
        padding: "22px 24px",
        borderRadius: 28,
        background: `radial-gradient(120% 100% at 22% 0%, rgba(${rgb},0.16), rgba(${rgb},0.03) 62%, transparent 100%)`,
        boxShadow: `0 0 42px rgba(${rgb},0.14) inset`,
        animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: 10, gap: 10 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "5px 12px", borderRadius: 999,
          background: `rgba(${rgb},0.14)`,
        }}>
          <StoneTotem color={c} size={10} />
          <span style={{ color: c, fontFamily: YFONT, fontSize: 9.5, letterSpacing: "0.22em",
            textTransform: "uppercase", fontWeight: 600 }}>
            {(insight.tag || "insight").toUpperCase()} · {insight.iq_level || ""}
          </span>
        </div>
        <div style={{ color: MUTED_36, fontFamily: YFONT, fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase" }}>
          {formatDate(insight.created_at)}
        </div>
      </div>
      <div style={{ color: INK, fontFamily: YFONT,
        fontSize: 17, letterSpacing: -0.2, lineHeight: 1.5, marginBottom: 10 }}>
        {insight.title}
      </div>
      <div style={{ color: MUTED_60, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
        {insight.body}
      </div>
      {insight.practice && (
        <div style={{
          marginTop: 14,
          padding: "14px 18px",
          borderRadius: 22,
          background: `radial-gradient(120% 100% at 22% 0%, rgba(${rgb},0.18), rgba(${rgb},0.03) 62%, transparent 100%)`,
          boxShadow: `0 0 24px rgba(${rgb},0.14) inset`,
          color: INK,
          fontSize: 13,
          lineHeight: 1.7,
        }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 6,
          }}>
            <StoneTotem color={c} size={10} />
            <span style={{ color: c, fontFamily: YFONT, fontSize: 9.5, letterSpacing: "0.24em",
              textTransform: "uppercase", fontWeight: 600 }}>
              Practice for your type
            </span>
          </div>
          <div>{insight.practice}</div>
        </div>
      )}

      {/* --- personal note (attached to this insight) --- */}
      <div style={{ marginTop: 14 }}>
        {!editing && !saved && (
          <button
            data-testid={`insight-add-note-${insight.insight_id}`}
            onClick={() => setEditing(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 16px", borderRadius: 999,
              background: `rgba(${rgb},0.10)`,
              boxShadow: `0 0 18px rgba(${rgb},0.16) inset`,
              border: "none",
              color: c, fontFamily: YFONT, fontSize: 10,
              letterSpacing: "0.22em", textTransform: "uppercase",
              cursor: "pointer",
            }}>
            <StoneTotem color={c} size={10} />
            + Note
          </button>
        )}
        {!editing && saved && (
          <div
            data-testid={`insight-note-view-${insight.insight_id}`}
            onClick={() => setEditing(true)}
            style={{
              cursor: "pointer",
              padding: "14px 18px",
              borderRadius: 22,
              background: `rgba(${rgb},0.06)`,
              boxShadow: `0 0 24px rgba(${rgb},0.10) inset`,
              color: INK, fontSize: 13, lineHeight: 1.6,
            }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <StoneTotem color={c} size={10} />
              <span style={{ color: c, fontFamily: YFONT, fontSize: 9.5, letterSpacing: "0.24em",
                textTransform: "uppercase", fontWeight: 600 }}>
                Note · yours
              </span>
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{note}</div>
          </div>
        )}
        {editing && (
          <div>
            <textarea
              data-testid={`insight-note-input-${insight.insight_id}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Your reflection on this insight…"
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "14px 18px", borderRadius: 20,
                background: `rgba(${rgb},0.06)`,
                boxShadow: `0 0 24px rgba(${rgb},0.14) inset`,
                border: "none",
                color: INK, fontFamily: YFONT, fontSize: 13,
                lineHeight: 1.65, resize: "vertical", outline: "none",
                minHeight: 88,
              }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button
                data-testid={`insight-note-save-${insight.insight_id}`}
                onClick={save}
                disabled={saving}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "8px 18px", borderRadius: 999,
                  background: `radial-gradient(120% 160% at 30% 0%, rgba(${rgb},0.34), rgba(${rgb},0.08))`,
                  boxShadow: `0 0 22px rgba(${rgb},0.28)`,
                  border: "none",
                  color: INK, fontFamily: YFONT, fontSize: 10,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  cursor: saving ? "default" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}>
                <StoneTotem color={c} size={10} />
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                data-testid={`insight-note-cancel-${insight.insight_id}`}
                onClick={() => { setEditing(false); setNote(insight.user_note || ""); }}
                style={{
                  padding: "8px 18px", borderRadius: 999,
                  background: "var(--fg-05)",
                  border: "none",
                  color: MUTED_60, fontFamily: YFONT, fontSize: 10,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  cursor: "pointer",
                }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
